/**
 * A bounded diagnostic event log for browser development builds. Modules such
 * as Voice Mode playback and microphone analysis use `makeDevEventLog` to put
 * timestamped events in both the console and a named `window` property. In
 * production and tests, the returned logger does nothing.
 */

/** One timestamped diagnostic event stored in a development-only window log */
type DevEventLogEntry = {
  /** Short machine-readable event name */
  event: string;
  /** Wall-clock ISO timestamp for comparison with application logs */
  at: string;
  /** Monotonic page-relative time for measuring intervals */
  performanceMs: number;
} & Record<string, unknown>;

/**
 * Create a development-only event logger. Each call writes the event name and
 * metadata to `console.info` with `consoleTag`, and retains the newest
 * `capacity` entries on `window[windowKey]`. In production and tests, the
 * returned function does nothing.
 */
export function makeDevEventLog(
  consoleTag: string,
  windowKey: string,
  capacity: number
): (event: string, metadata?: Record<string, unknown>) => void {
  if (!import.meta.env.DEV || import.meta.env.MODE === 'test') {
    return () => {};
  }

  return (event, metadata = {}) => {
    const entry: DevEventLogEntry = {
      ...metadata,
      event,
      at: new Date().toISOString(),
      performanceMs: Math.round(performance.now()),
    };
    console.info(consoleTag, entry);

    const debugWindow = window as unknown as Record<
      string,
      DevEventLogEntry[] | undefined
    >;
    debugWindow[windowKey] = [...(debugWindow[windowKey] ?? []), entry].slice(
      -capacity
    );
  };
}
